import ol = require("ol");
import Base = require("./base-command");
import Converter = require("../utils/ol3/converter");
import geo = require("../utils/geo");

/**
 * The logic necessary to receive a query response and turn it into features on the map is difficult to follow.
 * This command attempts to consolidate all of that logic into one readable location.
 * The purpose of this command is to put features on a specific layer using a specific symbology. 
 */

class Command extends Base<{
    in: any; // todo, describe the possibilities in an public interface
    out: { layers: Array<Rhythm.GIS.FeatureQueryResultLayer> }
}> {

    static about = `
Adds features to a specific layer using a specific symbology.
Features coming through this command are added to the vector layer identified by the _layer-name_ option.
Commands associated with the layer render in the [infoviewer](#app/controls/infoviewer).
The features default symbology is identified by the _symbology_ option.

Usage Example:

An originating event e.g. [ags-geoquery-locator](#app/commands/ags-geoquery-locator) place _layers_ on the bus:

    <EVENT about="When user clicks map perform a search for addresses in that area" event="reverse-geoquery" id="ags-address-reverse-locator" mid="app/commands/ags-geoquery-locator" type="spatial">
    <OPTIONS>
        <Values id="query-service" value="https://usalvqa1.infor.com/arcgis/rest/services/IPS850/QASQL4850/MapServer" />
        <Values id="key-template" value="&lt;%=FID%&gt;" />
        <Values id="layers" value="1" />
        <Values about="once the addresses are found notify the handler that will add them to the address layer" id="event" value="add-to-address-layer" />
    </OPTIONS>
    </EVENT>    

Allowing this handler to merge the features within the _layers_ collection with the target layer:
    
    <EVENT event="add-to-address-layer" id="add-to-address-layer-handler" mid="app/commands/add-features-to-layer">
    <OPTIONS>
        <Values id="layer-name" value="address-features" />
        <Values id="symbology" value="addresses" />
    </OPTIONS>
    </EVENT>
        
    `;

    static options = {
        "box": {
            "about": "When present, identifies the search area resulting in this merge request.",
        },
        "layer-name": {
            "about": "the target layer",
        },
        "layers": {
            "about": "the feature collection",
        },
        "pixel": {
            "about": "converts this pixel into a WGS84 coordinate and assigns to the features _image-center_ attribute.",
        },
        "symbology": {
            "about": "the symbology rule to invoke",
        },
        "target-geometry-type": {
            "about": "if point then convert polygon to point feature",
            "domain": "point|polyline|polygon",
        },
    }

    findOption(name: "pixel"): ol.Pixel;
    findOption(name: "box"): ol.Extent;
    findOption(name: "layer-name"): string;
    findOption(name: "layers"): Array<Rhythm.GIS.FeatureQueryResultLayer>;
    findOption(name: "symbology"): string;
    findOption(name: "target-geometry-type"): "point" | "polygon" | "polyline";
    findOption(name: string) {
        return super.findOption(name)
    }

    /**
     * Any features coming through this command get added to the vector layer identified by the 'layer' options
     * Any commands associated with the layer render in the info viewer
     * All symbology is restricted to the root symbology identified by the 'symbology' option 
     */
    execute() {
        var config = this.options;
        this.mediator.mapViewMap(map => {
            let symbology = this.findOption("symbology");
            let layerName = this.findOption("layer-name");
            if (layerName && symbology) {
                let layers = this.findOption("layers");
                if (layers) {
                    let targetGeomType = this.findOption("target-geometry-type");
                    let converter = new Converter();
                    layers = layers.map(layer => {
                        if (targetGeomType) {
                            layer.features.filter(f => f.geometryType !== targetGeomType).forEach(f => {
                                f.geometryType = targetGeomType;
                                switch (f.geometryType) {
                                    case "point":
                                        // TODO: probably should pick a point along a polyline, this assumes polygon.
                                        let poly = new ol.geom.Polygon(f.geometry);
                                        f.geometry = poly.getInteriorPoint().getCoordinates();
                                        break;
                                }
                            });
                        }
                        // preserve the click location as an extended attribute of the feature
                        let pixel = this.findOption("pixel");
                        if (pixel) {
                            let point = ol.extent.getCenter(geo.pixelToExtent(map, pixel));
                            let outSr = map.getView().getProjection().getCode();
                            point = geo.transform("EPSG:4326", outSr, [point])[0];
                            layer.features.forEach(f => {
                                f.attributes["image-center"] = JSON.stringify(point);
                            });
                        }
                        let configLayer = converter.toConfigLayer(layer);
                        configLayer.id = layerName;
                        configLayer.Options.Values.push({
                            id: "symbology",
                            value: symbology
                        });
                        return configLayer;
                    });
                    this.mediator.api.mergeLayers({ Layers: layers });
                }
            }
            this.trigger();
        });
    }

}

export = Command;